"use client";

import { createContext, useContext, useMemo } from "react";
import { useRouter } from "next/navigation";
import useSWR from "swr";

type AuthUser = {
  id: string;
  email: string;
  username?: string | null;
  status: string;
  trial_ends_at?: string | null;
  is_admin: boolean;
  account_setup_complete: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  isLoading: boolean;
  refresh: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL || "/api";

const fetcher = async (url: string) => {
  try {
    console.log("🔐 AuthProvider: Fetching", url);
    const res = await fetch(url, { credentials: "include" });
    console.log("🔐 AuthProvider: Response status:", res.status);
    if (!res.ok) throw new Error(`Auth error: ${res.status}`);
    const data = await res.json();
    console.log("🔐 AuthProvider: Response data:", data);
    const user = data.user; // Extract user from {user: user_data} format
    console.log("🔐 AuthProvider: Extracted user:", user);
    return user;
  } catch (error) {
    // Silently handle network errors for auth checks
    console.debug('🔐 AuthProvider: Auth check failed:', error);
    return null;
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { data, isLoading, mutate } = useSWR<AuthUser | null>(`${apiBase}/auth/me`, fetcher, {
    revalidateOnFocus: false,
  });

  const value = useMemo<AuthContextValue>(() => {
    return {
      user: data ?? null,
      isLoading,
      refresh: () => mutate(),
      logout: async () => {
        await fetch(`${apiBase}/auth/logout`, { method: "POST", credentials: "include" });
        await mutate(null);
        router.push("/");
      },
    };
  }, [data, isLoading, mutate, router]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
